/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";
import { getSubdomain } from "./utils/subDomainHelper";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1_signupsignin1",
    forgotPassword: "B2C_1_reset_v3",
    editProfile: "B2C_1_EditProfile",
  },
  authorities: {
    signUpSignIn: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_signupsignin1",
    },
    forgotPassword: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_reset_v3",
    },
    editProfile: {
      authority:
        "https://6thSenseRiskMultiTenantDev.b2clogin.com/6thSenseRiskMultiTenantDev.onmicrosoft.com/B2C_1_EditProfile",
    },
  },
  authorityDomain: "6thSenseRiskMultiTenantDev.b2clogin.com",
};

const siteConfig = require("./siteConfig.json");

const subdomain = getSubdomain().toLowerCase();
export const siteData = siteConfig[subdomain];

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    // clientId: '79d8f64c-cf7c-4aa4-8a3d-68ab11b17c36', // This is the ONLY mandatory field that you need to supply.
    clientId: siteData?.clientId ?? "79d8f64c-cf7c-4aa4-8a3d-68ab11b17c36",
    authority: siteData?.authTenantLogin ?? b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [siteData?.authTenantLogin] ?? [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: siteData?.redirectUri ?? "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: siteData?.redirectUri ?? "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            break;

          default:
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiTodoList: {
    endpoint: process.env.REACT_APP_API_URL,
    scopes: {
      read: [
        "https://6thSenseRiskMultiTenantDev.onmicrosoft.com/6f4b368e-6a5d-4585-889f-146c176f1ebc/ToDoList.Read",
      ],
      write: [
        "https://6thSenseRiskMultiTenantDev.onmicrosoft.com/6f4b368e-6a5d-4585-889f-146c176f1ebc/ToDoList.ReadWrite",
      ],
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [siteData?.apiScopes],
};
